// Gestão de Assinaturas Ativas
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Select } from '../../../components/common/Select';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Badge } from '../../../components/common/Badge';
import { Plus, Edit, CreditCard, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { getAssinaturas, createAssinatura, updateAssinatura, deleteAssinatura } from '../../../services/assinaturasService';
import { getPacotes } from '../../../services/pacotesService';
import { getContas } from '../../../services/contasService';

interface Assinatura {
  id: string;
  contaId: string;
  pacoteId: string;
  pacoteNome: string;
  status: 'ativa' | 'cancelada' | 'suspensa' | 'vencida';
  dataInicio: string;
  dataFim: string | null;
  valorMensal: number;
  formaPagamento: string | null;
  proximaCobranca: string | null;
}

export function Assinaturas() {
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [assinaturaSelecionada, setAssinaturaSelecionada] = useState<Assinatura | null>(null);
  const [assinaturas, setAssinaturas] = useState<Assinatura[]>([]);
  const [pacotes, setPacotes] = useState<any[]>([]);
  const [contas, setContas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    contaId: '',
    pacoteId: '',
    cicloPagamento: 'mensal' as 'mensal' | 'anual',
    status: 'ativa' as 'ativa' | 'cancelada' | 'suspensa' | 'vencida',
    formaPagamento: '',
    dataInicio: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    await Promise.all([
      carregarAssinaturas(),
      carregarPacotes(),
      carregarContas(),
    ]);
  };

  const carregarPacotes = async () => {
    try {
      const pacotesData = await getPacotes();
      setPacotes(pacotesData);
    } catch (err: any) {
      console.error('[Assinaturas] Erro ao carregar pacotes:', err);
    }
  };

  const carregarContas = async () => {
    try {
      const contasData = await getContas();
      setContas(contasData);
    } catch (err: any) {
      console.error('[Assinaturas] Erro ao carregar contas:', err);
    }
  };

  const carregarAssinaturas = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Buscar assinaturas reais do banco
      const assinaturasData = await getAssinaturas();
      setAssinaturas(assinaturasData);
    } catch (err: any) {
      console.error('[Assinaturas] Erro ao carregar assinaturas:', err);
      setError(err.message || 'Erro ao carregar assinaturas');
    } finally {
      setLoading(false);
    }
  };

  const assinaturasFiltradas = assinaturas.filter((a) => {
    const matchBusca = !busca || a.pacoteNome.toLowerCase().includes(busca.toLowerCase());
    const matchStatus = !filtroStatus || a.status === filtroStatus;
    return matchBusca && matchStatus;
  });

  const abrirModal = (assinatura?: Assinatura) => {
    if (assinatura) {
      setAssinaturaSelecionada(assinatura);
      // Determinar ciclo de pagamento baseado na diferença entre data fim e início
      const dataInicio = new Date(assinatura.dataInicio);
      const dataFim = assinatura.dataFim ? new Date(assinatura.dataFim) : null;
      let cicloPagamento: 'mensal' | 'anual' = 'mensal';
      if (dataFim) {
        const diffMonths = (dataFim.getTime() - dataInicio.getTime()) / (1000 * 60 * 60 * 24 * 30);
        cicloPagamento = diffMonths >= 11 ? 'anual' : 'mensal';
      }
      
      setFormData({
        contaId: assinatura.contaId,
        pacoteId: assinatura.pacoteId,
        cicloPagamento,
        status: assinatura.status,
        formaPagamento: assinatura.formaPagamento || '',
        dataInicio: assinatura.dataInicio.includes('T') ? assinatura.dataInicio.split('T')[0] : assinatura.dataInicio,
      });
    } else {
      setAssinaturaSelecionada(null);
      setFormData({
        contaId: '',
        pacoteId: '',
        cicloPagamento: 'mensal',
        status: 'ativa',
        formaPagamento: '',
        dataInicio: new Date().toISOString().split('T')[0],
      });
    }
    setIsModalOpen(true);
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setAssinaturaSelecionada(null);
  };

  const handleSalvar = async () => {
    try {
      if (!formData.contaId || !formData.pacoteId) {
        setError('Selecione uma conta e um pacote');
        return;
      }

      setIsSaving(true);
      setError(null);

      const pacoteSelecionado = pacotes.find(p => p.id === formData.pacoteId);
      if (!pacoteSelecionado) {
        setError('Pacote não encontrado');
        return;
      }

      // Calcular valor baseado no ciclo de pagamento
      const valorMensal = formData.cicloPagamento === 'mensal' 
        ? pacoteSelecionado.valorMensal 
        : pacoteSelecionado.valorAnual / 12;

      // Calcular data fim e próxima cobrança
      const dataInicio = new Date(formData.dataInicio);
      const dataFim = new Date(dataInicio);
      dataFim.setMonth(dataFim.getMonth() + (formData.cicloPagamento === 'mensal' ? 1 : 12));
      
      const proximaCobranca = new Date(dataInicio);
      proximaCobranca.setMonth(proximaCobranca.getMonth() + (formData.cicloPagamento === 'mensal' ? 1 : 12));

      if (assinaturaSelecionada) {
        // Atualizar assinatura existente
        await updateAssinatura(assinaturaSelecionada.id, {
          contaId: formData.contaId,
          pacoteId: formData.pacoteId,
          status: formData.status,
          dataInicio: formData.dataInicio,
          dataFim: dataFim.toISOString().split('T')[0],
          valorMensal,
          formaPagamento: formData.formaPagamento || null,
          proximaCobranca: proximaCobranca.toISOString().split('T')[0],
        });
      } else {
        // Criar nova assinatura
        await createAssinatura({
          contaId: formData.contaId,
          pacoteId: formData.pacoteId,
          status: formData.status,
          dataInicio: formData.dataInicio,
          dataFim: dataFim.toISOString().split('T')[0],
          valorMensal,
          formaPagamento: formData.formaPagamento || null,
          proximaCobranca: proximaCobranca.toISOString().split('T')[0],
        });
      }

      await carregarAssinaturas();
      fecharModal();
    } catch (err: any) {
      console.error('[Assinaturas] Erro ao salvar assinatura:', err);
      setError(err.message || 'Erro ao salvar assinatura');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'danger' | 'warning' | 'default'> = {
      ativa: 'success',
      cancelada: 'danger',
      suspensa: 'warning',
      vencida: 'danger',
    };
    return variants[status] || 'default';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ativa':
        return <CheckCircle className="w-4 h-4" />;
      case 'cancelada':
      case 'vencida':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Assinaturas' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Assinaturas' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestão de Assinaturas</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerenciar assinaturas ativas da plataforma</p>
        </div>
        <Button variant="primary" onClick={() => abrirModal()}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Assinatura
        </Button>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Filtros */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Buscar"
            placeholder="Pacote, conta..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select
            label="Status"
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            options={[
              { value: '', label: 'Todos' },
              { value: 'ativa', label: 'Ativa' },
              { value: 'cancelada', label: 'Cancelada' },
              { value: 'suspensa', label: 'Suspensa' },
              { value: 'vencida', label: 'Vencida' },
            ]}
          />
        </div>
      </Card>

      {/* Tabela */}
      <Card>
        <Table
          data={assinaturasFiltradas}
          columns={[
            {
              key: 'pacoteNome',
              label: 'Pacote',
              render: (item: Assinatura) => (
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">{item.pacoteNome}</span>
                </div>
              ),
            },
            {
              key: 'contaId',
              label: 'Conta ID',
              render: (item: Assinatura) => (
                <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                  {item.contaId.slice(0, 8)}...
                </span>
              ),
            },
            {
              key: 'valorMensal',
              label: 'Valor Mensal',
              render: (item: Assinatura) => formatCurrency(item.valorMensal),
            },
            {
              key: 'status',
              label: 'Status',
              render: (item: Assinatura) => (
                <Badge variant={getStatusBadge(item.status)}>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(item.status)}
                    <span>{item.status}</span>
                  </div>
                </Badge>
              ),
            },
            {
              key: 'dataInicio',
              label: 'Data Início',
              render: (item: Assinatura) => formatDate(item.dataInicio),
            },
            {
              key: 'proximaCobranca',
              label: 'Próxima Cobrança',
              render: (item: Assinatura) => item.proximaCobranca ? formatDate(item.proximaCobranca) : '-',
            },
            {
              key: 'actions',
              label: 'Ações',
              render: (item: Assinatura) => (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => abrirModal(item)}
                    className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                    title="Editar assinatura"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Modal de Edição/Criação */}
      <Modal
        isOpen={isModalOpen}
        onClose={fecharModal}
        title={assinaturaSelecionada ? 'Editar Assinatura' : 'Nova Assinatura'}
        size="lg"
      >
        <div className="space-y-4">
          <Select
            label="Conta (Empresa)"
            value={formData.contaId}
            onChange={(e) => setFormData({ ...formData, contaId: e.target.value })}
            options={[
              { value: '', label: 'Selecione uma conta' },
              ...contas.map((conta) => ({
                value: conta.id,
                label: `${conta.nome} ${conta.email ? `(${conta.email})` : ''}`,
              })),
            ]}
            required
          />

          <Select
            label="Pacote"
            value={formData.pacoteId}
            onChange={(e) => setFormData({ ...formData, pacoteId: e.target.value })}
            options={[
              { value: '', label: 'Selecione um pacote' },
              ...pacotes.filter(p => p.ativo).map((pacote) => ({
                value: pacote.id,
                label: `${pacote.nome} - ${formatCurrency(pacote.valorMensal)}/mês ou ${formatCurrency(pacote.valorAnual)}/ano`,
              })),
            ]}
            required
          />

          {formData.pacoteId && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Valor do Pacote:
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {formData.cicloPagamento === 'mensal' 
                  ? formatCurrency(pacotes.find(p => p.id === formData.pacoteId)?.valorMensal || 0)
                  : formatCurrency(pacotes.find(p => p.id === formData.pacoteId)?.valorAnual || 0)
                } / {formData.cicloPagamento === 'mensal' ? 'mês' : 'ano'}
              </p>
            </div>
          )}

          <Select
            label="Ciclo de Pagamento"
            value={formData.cicloPagamento}
            onChange={(e) => setFormData({ ...formData, cicloPagamento: e.target.value as 'mensal' | 'anual' })}
            options={[
              { value: 'mensal', label: 'Mensal' },
              { value: 'anual', label: 'Anual (10% de desconto)' },
            ]}
            required
          />

          <Input
            label="Data de Início"
            type="date"
            value={formData.dataInicio}
            onChange={(e) => setFormData({ ...formData, dataInicio: e.target.value })}
            required
          />

          <Select
            label="Forma de Pagamento"
            value={formData.formaPagamento}
            onChange={(e) => setFormData({ ...formData, formaPagamento: e.target.value })}
            options={[
              { value: '', label: 'Selecione' },
              { value: 'cartao_credito', label: 'Cartão de Crédito' },
              { value: 'cartao_debito', label: 'Cartão de Débito' },
              { value: 'boleto', label: 'Boleto' },
              { value: 'pix', label: 'PIX' },
              { value: 'transferencia', label: 'Transferência Bancária' },
            ]}
          />

          <Select
            label="Status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
            options={[
              { value: 'ativa', label: 'Ativa' },
              { value: 'cancelada', label: 'Cancelada' },
              { value: 'suspensa', label: 'Suspensa' },
              { value: 'vencida', label: 'Vencida' },
            ]}
            required
          />

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="secondary" onClick={fecharModal} disabled={isSaving}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSalvar} disabled={isSaving}>
              {isSaving ? 'Salvando...' : assinaturaSelecionada ? 'Atualizar' : 'Criar Assinatura'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

